import { useQuery } from '@apollo/client';
import { graphql } from '../gql';
import { useEffect, useMemo } from 'react';
import { GetProductsQuery } from '../gql/graphql';
import { getPaginatedNodes } from './utils';
import {
  handleValidationErrors,
  validatePaginatedEdgesResponse,
  yep,
} from '@converge/utils';

const GET_PRODUCTS_SCHEMA = yep.object().shape({
  products: validatePaginatedEdgesResponse(
    'Product',
    yep.object().shape({
      name: yep.string().nullToDefault('N/A').required(),
      uuid: yep.string().required(),
    })
  ),
});

const GET_PRODUCTS = graphql(`
  query getProducts($after: Int, $first: Int) {
    products(pagination: { first: $first, after: $after }) {
      pageInfo {
        totalResults
        hasNextPage
        pageNumber
      }
      edges {
        cursor
        node {
          name
          uuid
        }
      }
    }
  }
`);

export function useGetProductsQuery(first = 2000) {
  const { loading, error, data, fetchMore } = useQuery(GET_PRODUCTS, {
    variables: {
      after: 0,
      first,
    },
    onCompleted: (data) => {
      if (data.products.pageInfo.hasNextPage) {
        fetchMore({
          variables: {
            after: data.products.pageInfo.pageNumber + 1,
            first,
          },
          updateQuery: (previousData, { fetchMoreResult: newData }) => {
            const mergedData: GetProductsQuery = {
              ...previousData,
              ...newData,
              products: {
                ...previousData.products,
                ...newData.products,
                pageInfo: {
                  ...previousData.products.pageInfo,
                  ...newData.products.pageInfo,
                },
                edges: [
                  ...previousData.products.edges,
                  ...newData.products.edges,
                ],
              },
            };
            return mergedData;
          },
        });
      }
    },
  });

  const products = useMemo(() => {
    if (!data) {
      return [];
    }
    try {
      const validatedData = GET_PRODUCTS_SCHEMA.validateSync(data, {
        strict: false,
        abortEarly: false,
      });

      return validatedData.products.edges.map((edge) => edge.node);
    } catch (cause) {
      console.warn('useGetProductQuery', cause);
      return getPaginatedNodes(data.products);
    }
  }, [data]);

  useEffect(() => {
    if (!data) {
      return;
    }

    try {
      GET_PRODUCTS_SCHEMA.validateSync(data, {
        strict: true,
      });
    } catch (cause) {
      handleValidationErrors({
        cause,
        source: 'useGetProductsQuery',
        inputData: data,
        throwError: false,
      });
    }
  }, [data]);

  return {
    loading,
    error,
    data,
    products,
  };
}

export type ProductDetails = ReturnType<
  typeof useGetProductsQuery
>['products'][0];
